import { FC } from "react";
import { Page } from "components/templates/Page";
import { ProductIntro } from "components/molecules/ProductIntro";
import { HeroSection } from "components/organisms/HeroSection";
import { NewZigZag } from "components/molecules/NewZigZag";
import { KeyResourcesItemProps } from "components/atoms/KeyResourcesItem";
import { CenteredButtonWrap, TrophyYugi, Tooltip } from "./styles";
import { color } from "@styles";
import { BetterButton } from "components/atoms/BetterButton";
import Link from "next/link";
import { StaticRoutes } from "@routes";
import { Clouds } from "./Clouds";
import { LatestCaseStudy } from "utils/server";
import { ResponsiveZigZagImage } from "components/atoms/ResponsiveZigZagImage";
import { InsuranceProvidersBar } from "components/organisms/InsuranceProvidersBar";
import { useLottie } from "hooks/useLottie";
import { P1 } from "components/atoms/Paragraph";
import { Anchor } from "components/atoms/Anchor";
import { StatBox } from "components/atoms/StatBox";
import { TabSwitcherProducts } from "components/templates/TabSwitcherProducts";
import { data } from "./tabs";
import BottomSection from "components/organisms/BottomSection";
import { YULIFE_DATA } from "utils/_constants";

interface InsuranceCarriersProps {
  keyResources: KeyResourcesItemProps[];
  latestCaseStudy: LatestCaseStudy;
}

const InsuranceCarriers: FC<InsuranceCarriersProps> = ({ keyResources, latestCaseStudy }) => {
  const animationRef = useLottie("/static/lottie/YugiTrophy.json");

  return (
    <>
      <Page
        navColor="transparent"
        latestCaseStudy={latestCaseStudy}
        hero={
          <HeroSection
            title="A win-win partnership"
            subHeading="Create a market-leading insurance ecosystem that drives new business, boosts customer loyalty and reduces population risk."
            eyeBrow="YuLife for carriers"
            eyeBrowColour={color.yulifePink.P100}
            backgroundColor={color.S310}
            titleMaxWidth="35rem"
            clouds={<Clouds />}
            buttonText="Speak to our team"
            image={<TrophyYugi ref={animationRef} />}
            textPt="4rem"
            alt="An illustration of a giraffe running about freely in the Yuniverse."
            statBar={{
              backgroundColor: color.S3100,
              heading: "YuLife’s proven growth impact",
              stats: [
                {
                  value: "11x",
                  description: "monthly engagement vs traditional wellbeing apps",
                  tooltip: YULIFE_DATA,
                  maxWidth: "12rem",
                },
                {
                  value: "86",
                  description: "NPS  (vs 27 industry avg)",
                  tooltip: (
                    <Tooltip>
                      <P1 pt="0" pb="0">
                        {YULIFE_DATA}
                      </P1>
                      <Anchor href="customergauge.com">customergauge.com</Anchor>
                    </Tooltip>
                  ),
                  marginRight: "-0.5rem",
                  maxWidth: "12rem",
                },
                {
                  value: "98%",
                  description: "policy renewal rate (vs 82% market avg)",
                  tooltip: YULIFE_DATA,
                  maxWidth: "12rem",
                },
              ],
            }}
          >
            <InsuranceProvidersBar />
          </HeroSection>
        }
        fullScreenHero
      >
        <ProductIntro
          title="This is game-changing insurance"
          body="YuLife is an insurance technology and wellbeing solution. We provide companies and individuals with an all-in-one wellbeing offering: bringing together an ecosystem of insurance, rewards and benefits in an award-winning gamified app."
          pt="7.5rem"
        />
        <NewZigZag
          eyebrow="Revenue Growth "
          eyebrowPb="1rem"
          eyebrowColor="#28A87F"
          title="Win more business"
          description={
            <>
              <P1 pb="1.5rem" pt="0">
                Attract new clients, unlock additional revenue and boost customer loyalty by
                offering a game-changing insurance model that delivers proven ROI.
              </P1>
              <StatBox
                stat="8x"
                copy="Growth vs Traditional insurers"
                tooltip="Corporate Adviser Workplace Protection and Wellbeing Report June 2023"
                statColor="#28A87F"
              />
            </>
          }
          centerImg
          align="left"
          pt="5rem"
          pb="0rem"
        >
          <ResponsiveZigZagImage
            src="/static/pages/businesses/yulife-us-today-screen-action.png"
            alt="An action shot of a person holding a phone looking at the YuScreen in the YuLife app. "
          />
        </NewZigZag>
        <NewZigZag
          eyebrow="Customer Satisfaction "
          eyebrowPb="1rem"
          eyebrowColor={color.S2100}
          title="Deepen your customer relationships"
          description={
            <>
              <P1 pb="1.5rem" pt="0">
                Businesses and their people love YuLife. With an industry-leading NPS score of 86
                and Trustpilot score of 4.8, YuLife delivers the most loved insurance and wellbeing
                experience.
              </P1>
              <StatBox
                stat="93%"
                copy="highly engaged users"
                tooltip="People who have downloaded the app in the last year and have been active in the last 30 days"
                statColor={color.S2100}
              />
            </>
          }
          centerImg
          align="left"
          reverse
          pt="5rem"
        >
          <ResponsiveZigZagImage
            src="/static/pages/insurance-carriers/yulife-us-employee-leaderboard.png"
            alt="An employee sitting in a chair showing her phone and position in the leaderboard in the YuLife app."
          />
        </NewZigZag>
        <NewZigZag
          eyebrow="Risk Mitigation"
          eyebrowPb="1rem"
          eyebrowColor={color.S3100}
          title="Create healthier individuals"
          description={
            <>
              <P1 pb="1.5rem" pt="0">
                Insurance premiums are rising due to ill-health. Improve population health risk with
                an accessible and inclusive wellbeing tool that drives unprecedented engagement
                levels.
              </P1>
              <StatBox
                stat="5%"
                copy="loss ratio reduction through lower risk population"
                tooltip={YULIFE_DATA}
                statColor={color.S3100}
                boxWidth="30rem"
              />
            </>
          }
          centerImg
          align="left"
          pb="5rem"
        >
          <ResponsiveZigZagImage
            src="/static/pages/insurance-carriers/yulife-us-meditation-action.png"
            alt="A user holding an iPhone about to start a meditation in the YuLife app. "
          />
        </NewZigZag>
        <CenteredButtonWrap>
          <Link href={StaticRoutes.getAQuote}>
            <BetterButton text="Speak to our team" />
          </Link>
        </CenteredButtonWrap>
        <TabSwitcherProducts
          title="Healthier people means healthier business"
          content="YuLife inspires health and wellbeing for everyone, by encouraging daily healthy activity - from walking to mindfulness - and rewarding you for it."
          tabs={data.tabs}
        />
        <BottomSection
          keyResources={keyResources}
          keyResourcesTitle="News & Updates"
          keyResourcesType="news-and-events"
          showRequestCallback={true}
          requestCallbackProps={{
            subtitle:
              "Find out more about the YuLife proposition and discuss how together we could bring something new and exciting to your clients.",
          }}
        />
      </Page>
    </>
  );
};

export default InsuranceCarriers;
